<template>
    <div class="bridge-loading-page">
        <div class="bridge-loading-content">
            <div class="bridge-spinner" aria-hidden="true"></div>
            <h1 class="bridge-status">{{ statusText }}</h1>
            <p v-if="subStatusText" class="bridge-sub-status">{{ subStatusText }}</p>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { postAxios } from "@/lib/AxiosCall";
import { encryptAES256 } from "@/lib/crypto";

function trim(value) {
    return String(value || "").trim();
}

/**
 * ZONE_HANDLERS — subId별 후처리 전략 테이블
 * 회원 조회(foodcon/id)는 공통. 후처리만 분기.
 */
const ZONE_HANDLERS = {
    BENE_1: {
        type: "EMBERSHIP_SSO",
    },
    BENE_2: {
        type: "SESSION_KEY",
        apiUrl: "https://partner.hivitamin.co.kr/api/foodcon/prepare-ci/",
    },
    // BENE_3: { type: 'CUSTOM', handler: async (member, params) => { ... } },
};
const DEFAULT_HANDLER = { type: "DIRECT_REDIRECT" };

function getZoneHandler(subId) {
    return ZONE_HANDLERS[subId] || DEFAULT_HANDLER;
}

export default {
    name: "HFO_BG_FC_001",
    data() {
        return {
            statusText: "회원 확인 중입니다...",
            subStatusText: "잠시만 기다려주세요.",
            params: {
                userId: "",
                ci: "",
                targetUrl: "",
                clickId: "",
                subId: "",
                adId: "",
                entUuid: "",
                entryType: "",
                name: "",
                phone: "",
                ssoStartUrl: "",
                returnUrl: "",
                tenantCode: "",
                brokerAlias: "",
                direction: "",
                purpose: "",
                prepareCiAlert: "",
            },
        };
    },
    async mounted() {
        this.parseParams();

        if (!this.params.userId && !this.params.ci) {
            this.statusText = "잘못된 접근입니다.";
            this.subStatusText = "userId 또는 ci가 필요합니다.";
            return;
        }

        this.saveCpsEntryContext();
        await this.execute();
    },
    methods: {
        parseParams() {
            const q = this.$route?.query || {};
            this.params.userId = String(q.userId || "").trim();
            this.params.ci = String(q.ci || "").trim();
            this.params.targetUrl = String(q.targetUrl || "").trim();
            this.params.clickId = String(q.clickId || "").trim();
            this.params.subId = String(q.subId || "").trim();
            this.params.adId = String(q.adId || "").trim();
            this.params.entUuid = String(q.entUuid || "").trim();
            this.params.entryType = String(q.entryType || "cps").trim();
            this.params.name = String(q.name || "").trim();
            this.params.phone = String(q.phone || "").replace(/[^0-9]/g, "");
            this.params.ssoStartUrl = String(q.ssoStartUrl || "").trim();
            this.params.returnUrl = String(q.returnUrl || "").trim();
            this.params.tenantCode = String(q.tenantCode || "").trim();
            this.params.brokerAlias = String(q.brokerAlias || "").trim();
            this.params.direction = String(q.direction || "").trim();
            this.params.purpose = String(q.purpose || "").trim();
            this.params.prepareCiAlert = String(q.prepareCiAlert || q.testAlert || "").trim();
        },

        saveCpsEntryContext() {
            const p = this.params;
            const context = {
                entryType: p.entryType || "cps",
                userId: p.userId,
                entUuid: p.entUuid,
                frcUuid: "",
                targetUrl: p.targetUrl,
                clickId: p.clickId,
                adId: p.adId,
                subId: p.subId,
                ci: p.ci || "",
                ssoStartUrl: p.ssoStartUrl,
                returnUrl: p.returnUrl,
                tenantCode: p.tenantCode,
                brokerAlias: p.brokerAlias,
                direction: p.direction,
                purpose: p.purpose,
                returnTo: "/HFO_BG_FC_001?" + this.buildReturnQueryString(),
                createdAt: new Date().toISOString(),
            };
            try {
                const json = JSON.stringify(context);
                sessionStorage.setItem("cps_entry_context", json);
                localStorage.setItem("cps_entry_context", json);
            } catch (_e) {
                // ignore
            }
        },

        buildReturnQueryString() {
            const p = this.params;
            const qs = new URLSearchParams();
            if (p.userId) qs.set("userId", p.userId);
            if (p.ci) qs.set("ci", p.ci);
            if (p.targetUrl) qs.set("targetUrl", p.targetUrl);
            if (p.clickId) qs.set("clickId", p.clickId);
            if (p.subId) qs.set("subId", p.subId);
            if (p.adId) qs.set("adId", p.adId);
            if (p.entUuid) qs.set("entUuid", p.entUuid);
            if (p.entryType) qs.set("entryType", p.entryType);
            if (p.ssoStartUrl) qs.set("ssoStartUrl", p.ssoStartUrl);
            if (p.returnUrl) qs.set("returnUrl", p.returnUrl);
            if (p.tenantCode) qs.set("tenantCode", p.tenantCode);
            if (p.brokerAlias) qs.set("brokerAlias", p.brokerAlias);
            if (p.direction) qs.set("direction", p.direction);
            if (p.purpose) qs.set("purpose", p.purpose);
            if (p.prepareCiAlert) qs.set("prepareCiAlert", p.prepareCiAlert);
            return qs.toString();
        },

        getCpsEntryContext() {
            try {
                const raw = sessionStorage.getItem("cps_entry_context") || localStorage.getItem("cps_entry_context");
                return raw ? JSON.parse(raw) : null;
            } catch (_e) {
                return null;
            }
        },

        async execute() {
            const p = this.params;
            const handler = getZoneHandler(p.subId);

            // userId 없이 ci만 전달되는 앱 유입 케이스 처리
            if (!p.userId && p.ci) {
                try {
                    this.updateCpsEntryContextCi(p.ci);
                    if (handler.type === "EMBERSHIP_SSO") {
                        await this.handleEmembershipSso(p.ci);
                    } else if (handler.type === "SESSION_KEY") {
                        await this.handleSessionKey(handler, "", "", p.ci);
                    } else if (handler.type === "CUSTOM" && typeof handler.handler === "function") {
                        await handler.handler({ name: "", phone: "", ci: p.ci }, p);
                    } else {
                        this.handleDirectRedirectWithCi();
                    }
                } catch (error) {
                    console.error("HFO_BG_FC_001: ci-only 처리 실패", error);
                    this.statusText = "오류가 발생했습니다. 로그인 페이지로 이동합니다...";
                    this.subStatusText = "";
                    this.redirectToLoginFallback(300);
                }
                return;
            }

            try {
                // ── 1단계: 공통 — foodcon/id 회원 조회 ──
                this.statusText = "회원 확인 중입니다...";
                const encryptedUser = encryptAES256(p.userId);
                const res = await postAxios("/bgm/v1/foodcon/id", {
                    platformUser: encryptedUser,
                });

                const memberId = String((res && (res.id || res.ID)) || "").trim();
                const memberName = String((res && (res.name || res.NAME || res.userName)) || p.name || "").trim();
                const memberPhone = String((res && (res.phone || res.PHONE || res.mobile)) || p.phone || "").replace(/[^0-9]/g, "");
                const kakaoMemberId = String(
                    (res && (res.kakaoMemberId || res.kakaoUserId || res.kakaoId || res.KAKAO_MEMBER_ID || res.KAKAO_ID || res.id || res.ID)) ||
                    "",
                ).trim();
                // ── 미가입자: 기존 가입 프로세스로 이동 ──
                if (!memberId) {
                    this.statusText = "가입 페이지로 이동합니다...";
                    this.subStatusText = "";
                    this.redirectToLoginFallback(300);
                    return;
                }

                // ── 2단계: subId별 후처리 분기 (ZONE_HANDLERS) ──
                // ci는 URL 우선, 없으면 회원조회 결과의 카카오회원 ID를 대체값으로 사용.
                const effectiveCi = p.ci || kakaoMemberId;
                if (effectiveCi) {
                    this.updateCpsEntryContextCi(effectiveCi);
                }

                if (handler.type === "EMBERSHIP_SSO") {
                    await this.handleEmembershipSso(effectiveCi);
                } else if (handler.type === "SESSION_KEY") {
                    await this.handleSessionKey(handler, memberName, memberPhone, effectiveCi);
                } else if (handler.type === "CUSTOM" && typeof handler.handler === "function") {
                    await handler.handler({ name: memberName, phone: memberPhone, ci: effectiveCi }, p);
                } else {
                    // DIRECT_REDIRECT (default)
                    this.handleDirectRedirect();
                }
            } catch (error) {
                console.error("HFO_BG_FC_001: 회원 조회 실패", error);
                // 실패 시 기존 가입 페이지로 fallback
                this.statusText = "오류가 발생했습니다. 로그인 페이지로 이동합니다...";
                this.subStatusText = "";
                this.redirectToLoginFallback(1000);
            }
        },

        redirectToLoginFallback(delayMs = 300) {
            const loginUrl = this.buildLoginRedirectUrl();
            setTimeout(() => {
                window.location.href = loginUrl;
            }, delayMs);
        },

        updateCpsEntryContextCi(ci) {
            try {
                const ctx = this.getCpsEntryContext();
                if (ctx) {
                    ctx.ci = ci;
                    // returnTo는 재진입 시에만 필요하므로 기존 회원일 땐 그대로 유지
                    const json = JSON.stringify(ctx);
                    sessionStorage.setItem("cps_entry_context", json);
                    localStorage.setItem("cps_entry_context", json);
                }
            } catch (_e) {
                // ignore
            }
        },

        isPrepareCiAlertEnabled() {
            const byQuery = String(this.params.prepareCiAlert || "").trim().toLowerCase();
            if (["1", "y", "yes", "true", "on"].includes(byQuery)) {
                return true;
            }

            try {
                const byStorage = String(localStorage.getItem("foodcon_prepare_ci_alert") || "").trim().toLowerCase();
                return ["1", "y", "yes", "true", "on"].includes(byStorage);
            } catch (_e) {
                return false;
            }
        },

        showPrepareCiFailAlert(error) {
            let detail = "unknown";
            if (error?.response) {
                const status = String(error.response.status || "").trim();
                const message = trim(error.response.data?.message || error.response.data?.error || "") || "prepare-ci failed";
                detail = status ? `[${status}] ${message}` : message;
            } else if (error?.message) {
                detail = String(error.message);
            }

            window.alert(`로그인 준비 중 오류가 발생했습니다.\n다시 시도해 주세요.\n(${detail})`);
        },

        async handleSessionKey(handler, name, phone, ci) {
            this.statusText = "세션 준비 중입니다...";
            this.subStatusText = "";

            if (!ci) {
                console.warn("HFO_BG_FC_001: ci 없음 → 로그인 폴백");
                this.statusText = "로그인 페이지로 이동합니다...";
                this.subStatusText = "";
                this.redirectToLoginFallback(300);
                return;
            }

            try {
                const payload = { ci };
                if (name) payload.name = name;
                if (phone) payload.phone = phone;

                const res = await axios.post(
                    handler.apiUrl,
                    payload,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        timeout: 10000,
                    },
                );

                const sessionKey = String(res?.data?.sessionKey || "").trim();
                if (!sessionKey) {
                    console.warn("HFO_BG_FC_001: sessionKey 빈 값 → 로그인 폴백");
                    this.showPrepareCiFailAlert({ message: "sessionKey is empty" });
                    this.statusText = "로그인 페이지로 이동합니다...";
                    this.subStatusText = "";
                    this.redirectToLoginFallback(300);
                    return;
                }

                // 세션키 발급 성공 → 최종 URL 구성
                this.statusText = "페이지 이동 중입니다...";
                const finalUrl = this.buildSessionKeyRedirectUrl(sessionKey);
                this.cleanupAndRedirect(finalUrl);
            } catch (error) {
                console.error("HFO_BG_FC_001: prepare-ci 실패", error);
                this.showPrepareCiFailAlert(error);
                // prepare-ci 실패 시 포레스트커머스 직이동 금지
                this.statusText = "로그인 페이지로 이동합니다...";
                this.subStatusText = "";
                this.redirectToLoginFallback(300);
            }
        },

        buildEmembershipStartUrl(ci) {
            const p = this.params;
            let startUrl = trim(p.ssoStartUrl);
            let returnUrl = trim(p.returnUrl);
            let tenantCode = trim(p.tenantCode) || "FOODCON";
            let brokerAlias = trim(p.brokerAlias) || "foodcon-oidc";
            let direction = trim(p.direction) || "INBOUND";
            let purpose = trim(p.purpose) || "CONNECT";

            const targetUrl = trim(p.targetUrl);
            if (targetUrl) {
                try {
                    const parsed = new URL(targetUrl);
                    const pathname = trim(parsed.pathname).toLowerCase();
                    const isStartPath = pathname.endsWith("/api/v2/auth/sso/start");

                    if (isStartPath) {
                        if (!startUrl) {
                            startUrl = `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
                        }
                        if (!returnUrl) returnUrl = trim(parsed.searchParams.get("returnUrl"));
                        if (!brokerAlias) brokerAlias = trim(parsed.searchParams.get("brokerAlias")) || "foodcon-oidc";
                        if (!tenantCode) tenantCode = trim(parsed.searchParams.get("tenantCode")) || "FOODCON";
                        if (!direction) direction = trim(parsed.searchParams.get("direction")) || "INBOUND";
                        if (!purpose) purpose = trim(parsed.searchParams.get("purpose")) || "CONNECT";
                    } else if (!returnUrl) {
                        returnUrl = targetUrl;
                    }
                } catch (_e) {
                    if (!returnUrl) {
                        returnUrl = targetUrl;
                    }
                }
            }

            if (!startUrl) {
                startUrl = "https://qa.embership.co.kr/api/v2/auth/sso/start";
            }
            if (!returnUrl) {
                returnUrl = `${window.location.origin}/HFO_MP_005`;
            }

            const start = new URL(startUrl);
            start.searchParams.set("tenantCode", tenantCode);
            start.searchParams.set("brokerAlias", brokerAlias);
            start.searchParams.set("direction", direction);
            start.searchParams.set("purpose", purpose);
            start.searchParams.set("entryType", trim(p.entryType) || "cps");
            start.searchParams.set("returnUrl", returnUrl);
            if (ci) {
                start.searchParams.set("ci", ci);
            }
            return start.toString();
        },

        async handleEmembershipSso(ci) {
            this.statusText = "앰버십 인증 페이지로 이동합니다...";
            this.subStatusText = "";

            try {
                const ssoStartUrl = this.buildEmembershipStartUrl(ci);
                setTimeout(() => {
                    window.location.href = ssoStartUrl;
                }, 300);
            } catch (error) {
                console.error("HFO_BG_FC_001: embership sso start URL 생성 실패", error);
                // SSO 분기 실패 시 단일 폴백: 로그인 브릿지
                this.redirectToLoginFallback(300);
            }
        },

        buildSessionKeyRedirectUrl(sessionKey) {
            const targetUrl = this.params.targetUrl;
            if (!targetUrl) {
                return `https://shop.forestad.co.kr/foodcon?sessionKey=${encodeURIComponent(sessionKey)}&utm_source=foodcon`;
            }
            try {
                const url = new URL(targetUrl);
                url.searchParams.set("sessionKey", sessionKey);
                url.searchParams.set("utm_source", "foodcon");
                return url.toString();
            } catch (_e) {
                return `${targetUrl}${targetUrl.includes("?") ? "&" : "?"}sessionKey=${encodeURIComponent(sessionKey)}&utm_source=foodcon`;
            }
        },

        handleDirectRedirect() {
            this.statusText = "페이지 이동 중입니다...";
            this.subStatusText = "";
            const targetUrl = this.params.targetUrl || "https://shop.forestad.co.kr/foodcon";
            this.cleanupAndRedirect(targetUrl);
        },

        handleDirectRedirectWithCi() {
            this.statusText = "페이지 이동 중입니다...";
            this.subStatusText = "";
            const targetUrl = this.params.targetUrl || "https://shop.forestad.co.kr/foodcon";
            const ctx = this.getCpsEntryContext();
            const ci = ctx?.ci || "";

            let finalUrl = targetUrl;
            if (ci) {
                try {
                    const url = new URL(targetUrl);
                    url.searchParams.set("ci", ci);
                    finalUrl = url.toString();
                } catch (_e) {
                    finalUrl = `${targetUrl}${targetUrl.includes("?") ? "&" : "?"}ci=${encodeURIComponent(ci)}`;
                }
            }
            this.cleanupAndRedirect(finalUrl);
        },

        cleanupAndRedirect(url) {
            try {
                sessionStorage.removeItem("cps_entry_context");
                localStorage.removeItem("cps_entry_context");
            } catch (_e) {
                // ignore
            }
            setTimeout(() => {
                window.location.href = url;
            }, 300);
        },

        buildLoginRedirectUrl() {
            const p = this.params;
            const base = `${window.location.origin}/HFO_LO_001_FOODCON`;
            const qs = new URLSearchParams();
            qs.set("entryType", "cps");
            if (p.userId) qs.set("userId", p.userId);
            if (p.targetUrl) qs.set("targetUrl", p.targetUrl);
            if (p.clickId) qs.set("clickId", p.clickId);
            if (p.subId) qs.set("subId", p.subId);
            if (p.adId) qs.set("adId", p.adId);
            if (p.entUuid) qs.set("entUuid", p.entUuid);
            if (p.ssoStartUrl) qs.set("ssoStartUrl", p.ssoStartUrl);
            if (p.returnUrl) qs.set("returnUrl", p.returnUrl);
            if (p.tenantCode) qs.set("tenantCode", p.tenantCode);
            if (p.brokerAlias) qs.set("brokerAlias", p.brokerAlias);
            if (p.direction) qs.set("direction", p.direction);
            if (p.purpose) qs.set("purpose", p.purpose);
            if (p.prepareCiAlert) qs.set("prepareCiAlert", p.prepareCiAlert);
            return `${base}?${qs.toString()}`;
        },
    },
};
</script>

<style scoped>
.bridge-loading-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: #fff;
}

.bridge-loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
}

.bridge-spinner {
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #009ca6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.bridge-status {
    font-size: 18px;
    color: #374151;
    font-weight: 500;
    margin: 0;
}

.bridge-sub-status {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
}
</style>
